<script setup>
import { ref, computed, onMounted } from 'vue';
import Manufacturer from './Manufacturer.vue';
import ManufacturersFilter from './ManufacturersFilter.vue';
import ManufacturersDashboard from './ManufacturersDashboard.vue';
import { useManufacturersStore } from './stores/manufacturers';

const props = defineProps({
    mapSvg: { type: String, default: '' },
});

const cfg  = window.ebusManufacturers || {};
const i18n = cfg.i18n || {};

const store = useManufacturersStore();

// ── State ─────────────────────────────────────────────────────────────────────

const view        = ref('grid');
const detailsOpen = ref(false);

// ── Derived ───────────────────────────────────────────────────────────────────

const filteredManufacturers = computed(() => {
    if (store.activeFilter === '*') return store.manufacturers;
    return store.manufacturers.filter(m => {
        const cls = [
            ...m.bus_sizes.map(s => `filter--${s}`),
            ...(m.in_latin_america ? ['filter--latin-america'] : []),
            ...m.countries.map(c => `filter--${c.slug}`),
        ];
        return cls.includes(store.activeFilter);
    });
});

// ── Actions ───────────────────────────────────────────────────────────────────

async function onGridClick(m) {
    store.activeIndex         = store.manufacturers.indexOf(m);
    store.currentManufacturer = null;
    view.value                = 'dashboard';
    await store.loadDetail(m.id);
}

function onContentClick(e) {
    if (e.target.closest('.manufactors__read-more')) detailsOpen.value = true;
}

// ── Boot ──────────────────────────────────────────────────────────────────────

onMounted(() => store.loadManufacturers());
</script>

<template>
  <div class="section section--manufactors h-auto!">

    <!-- ── Grid View ──────────────────────────────────────────────────────── -->
    <div
      class="manufactors__container manufactors__container--grid"
      v-show="view === 'grid'"
    >
      <div class="manufactors__header">
        <h1 class="page__title">
          <span v-html="cfg.pageTitle"></span>
          <span
            class="manufactors__back"
            :style="{ display: detailsOpen ? 'inline' : 'none' }"
            @click="detailsOpen = false"
          >&lt;-</span>
        </h1>
        <div class="manufactors__about" v-html="cfg.pageContent" @click="onContentClick"></div>
      </div>

      <div class="manufactors__scroll">
        <div
          class="manufactors__details"
          :style="{ display: detailsOpen ? 'block' : 'none' }"
          v-html="cfg.details"
        ></div>

        <div
          class="manufactors__content"
          :style="{ display: detailsOpen ? 'none' : 'flex' }"
        >
          <!-- Grid -->
          <div class="manufactors__grid">
            <div
              v-for="m in filteredManufacturers"
              :key="m.id"
              :class="['manufactor__container',
                       ...m.bus_sizes.map(s => `filter--${s}`),
                       m.in_latin_america ? 'filter--latin-america' : '',
                       ...m.countries.map(c => `filter--${c.slug}`)]"
            >
              <Manufacturer
                :manufacturer="m"
                :zebra-logo-url="cfg.zebraLogoUrl"
                @click="onGridClick(m)"
              />
            </div>
          </div>

          <ManufacturersFilter />
        </div>
      </div>
    </div>

    <!-- ── Dashboard View ─────────────────────────────────────────────────── -->
    <ManufacturersDashboard
      v-if="view === 'dashboard'"
      :map-svg="mapSvg"
      @close="view = 'grid'"
    />

  </div>
</template>
